import { Skeleton } from "@credit-acceptance/uikit";
import { InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import { EmptyState, ErrorState } from "../../../../components/ui/centeredMessage";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../components/ui/tooltip";
import { getErrorMessage } from "../../../../lib/query/errors";
import { useActionPanelStore } from "../../../../stores/actionPanelStore";
import Section from "../../components/Section";
import { openDmsAdjustmentSheet } from "./api/openDmsAdjustmentSheet";
import { useDealStatus } from "./api/useDealStatus";
import FinancialBreakdown from "./components/FinancialBreakdown";
import MilestoneDates from "./components/MilestoneDates";

const smallTextClassName =
  "text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)";

interface DealStatusSectionProperties {
  approvalId: string;
}

function DealStatusSection({ approvalId }: DealStatusSectionProperties) {
  const { t } = useTranslation();
  const openInternalAudit = useActionPanelStore((state) => state.openInternalAudit);
  const { data, isLoading, isError, error } = useDealStatus(approvalId);
  const showCapsHistoryLink = data?.flags?.hasDealStatus === true;
  const showPreApprovalHistoryLink = data?.flags?.hasDealStatus === false;

  const isEmpty = !data?.dealStatus;
  const hasTpap = data?.flags?.anyAncillaryVendorTpap === true;
  const isPurchaseProgram = data?.flags?.purchaseProgram === true;

  return (
    <Section title={t("dealStatus.sectionTitle")}>
      {isLoading && (
        <div role="status" aria-label={t("dealStatus.loading")}>
          <Skeleton loading active paragraph={{ rows: 6 }} />
        </div>
      )}

      {isError && (
        <ErrorState message={t("dealStatus.loadError", { message: getErrorMessage(error) })} className="p-0" />
      )}

      {!isLoading && !isError && isEmpty && <EmptyState message={t("dealStatus.empty")} className="p-0" />}

      {!isLoading && !isError && data && (
        <>
          {!isEmpty && (
            <>
              <div className="gap-x-(--spacing-8) gap-y-(--spacing-4) grid grid-cols-1 sm:grid-cols-2">
                <FinancialBreakdown data={data} />
                <MilestoneDates dates={data.dealStatus?.milestoneDates} />
              </div>

              <div
                className={`border-border mt-(--spacing-4) pt-(--spacing-3) ${smallTextClassName} gap-(--spacing-1) flex flex-col border-t`}
              >
                <p>{t(isPurchaseProgram ? "dealStatus.purchasePriceFootnote" : "dealStatus.advanceFootnote")}</p>
                <p>
                  {t(isPurchaseProgram ? "dealStatus.amountPaidPurchaseFootnote" : "dealStatus.amountPaidFootnote")}
                </p>
              </div>
            </>
          )}

          {showCapsHistoryLink && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => openInternalAudit("status-history")}
                  className={`mt-(--spacing-2) gap-(--spacing-1) ${smallTextClassName} h-auto p-0 underline`}
                >
                  {t("dealStatus.capsHistoryLink")}
                  <InfoIcon className="size-3.5" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">{t("dealStatus.changeHistoryTooltip")}</TooltipContent>
            </Tooltip>
          )}
          {showPreApprovalHistoryLink && (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => openInternalAudit("pre-approval-history")}
              className={`mt-(--spacing-2) gap-(--spacing-1) ${smallTextClassName} h-auto p-0 underline`}
            >
              {t("dealStatus.preApprovalHistoryLink")}
              <InfoIcon className="size-3.5" aria-hidden="true" />
            </Button>
          )}

          {/* DMS Adjustment Sheet — hidden for TPAP-provided VSC/GAP */}
          {!isEmpty && !hasTpap && (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => openDmsAdjustmentSheet(approvalId)}
              className={`mt-(--spacing-2) ${smallTextClassName} h-auto p-0 underline`}
            >
              {t("dealStatus.dmsAdjustmentSheetLink")}
            </Button>
          )}
        </>
      )}
    </Section>
  );
}

export default DealStatusSection;
